import { Container, SceneObject, SoundSceneObject } from '@cgs/wge';
import { Lazy } from '@cgs/shared';
import { AbstractIconResourceProvider } from './abstract_icon_resource_provider';
import { InstantCoinsAnimationProvider } from './instant_coins_animation_provider';
import { WinMovieSceneProvider } from './win_movie_scene_provider';
import {
  T_AbstractIconResourceProvider,
  T_InstantCoinsAnimationProvider,
  T_WinMovieSceneProvider,
} from '../../type_definitions';
import { SoundInstance } from '../../reels_engine/sound_instance';

export class ResourcesComponent {
  public slot: SceneObject;
  public bonusHolder: SceneObject;
  public sounds: SceneObject;
  public bg: SceneObject;
  public footer: SceneObject | null = null;

  private slotHolder: SceneObject | null = null;
  private slotRoot: SceneObject | null = null;

  private readonly container: Container;
  private readonly rootObject: SceneObject;
  private readonly iconResourceProvider: Lazy<AbstractIconResourceProvider>;

  constructor(container: Container, root: SceneObject) {
    this.container = container;
    this.rootObject = root;
    this.iconResourceProvider = new Lazy(() =>
      this.container.resolve<AbstractIconResourceProvider>(T_AbstractIconResourceProvider)
    );
  }

  public get root(): SceneObject {
    return this.rootObject;
  }

  public get fadeStaticIcons(): SceneObject[] {
    return this.slot.findAllById('fade_static_icons');
  }

  public getIcon(iconId: string): SceneObject[] {
    return this.iconResourceProvider.call().getIconNodes(iconId) || [];
  }

  public getBluredIcon(iconId: string): SceneObject[] {
    return this.iconResourceProvider.call().getBluredIconNodes(iconId) || [];
  }

  public registerSlot(
    slot: SceneObject,
    holderId: string | null = null,
    machineNodeId: string | null = null
  ): void {
    const machineNode = machineNodeId ? slot.findById(machineNodeId) : slot;
    this.slot = machineNode ? machineNode : slot;
    const holder = holderId ? (this.root.findById(holderId) as SceneObject | null) : null;

    this.slotHolder = holder;
    this.slotRoot = slot;
    if (holder) {
      holder.addChild(slot);
    } else {
      this.root.addChild(slot);
    }
  }

  public registerBonusHolder(bonusHolder: SceneObject): void {
    this.bonusHolder = bonusHolder;
    this.root.addChild(bonusHolder);
  }

  public registerSound(sounds: SceneObject): void {
    this.sounds = sounds;
    this.root.addChild(sounds);
  }

  public getSoundById(soundId: string): SoundInstance {
    const soundContainer = this.sounds.findById(soundId);
    return new SoundInstance(
      soundContainer?.findAllByType(SoundSceneObject)[0] || null,
      soundContainer?.stateMachine || null
    );
  }

  public registerBack(bg: SceneObject): void {
    this.bg = bg;
    this.root.addChild(bg);
  }

  public registerFooter(footer: SceneObject): void {
    this.footer = footer;
  }

  public findStartReelNode(reelIndex: number): SceneObject {
    return this.slot.findById('slot_start_' + reelIndex.toString()) as SceneObject;
  }

  public unload(): void {
    const winMovieSceneProvider =
      this.container.resolve<WinMovieSceneProvider>(T_WinMovieSceneProvider);
    if (winMovieSceneProvider) {
      winMovieSceneProvider.unload();
    }

    const instantCoinsProvider = this.container.resolve<InstantCoinsAnimationProvider>(
      T_InstantCoinsAnimationProvider
    );
    if (instantCoinsProvider) {
      instantCoinsProvider.unload();
    }

    this.iconResourceProvider.call().unload();

    if (this.slotRoot) {
      this.slotRoot.active = false;
      if (this.slotHolder) {
        this.slotHolder.removeChild(this.slotRoot);
      } else {
        this.root.removeChild(this.slotRoot);
      }
    }

    if (this.sounds) {
      this.sounds.active = false;
      this.root.removeChild(this.sounds);
    }

    if (this.bg) {
      this.bg.active = false;
      this.root.removeChild(this.bg);
    }

    if (this.bonusHolder) {
      this.root.removeChild(this.bonusHolder);
    }

    this.slotRoot?.deinitialize();
    if (this.sounds) {
      this.sounds.deinitialize();
    }
    if (this.bg) {
      this.bg.deinitialize();
    }
    if (this.bonusHolder) {
      this.bonusHolder.deinitialize();
    }
  }
}
